// src/pages/admin/ProductManagementPage.js
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Modal, TextField, Select, MenuItem,
    InputLabel, FormControl, CircularProgress, Alert, FormControlLabel, Switch
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import api from '../../api/index.js';
import CompositeProductModal from '../../components/pos/CompositeProductModal.js';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 600 },
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
    maxHeight: '90vh',
    overflowY: 'auto'
};

const ProductManagementPage = () => {
    // --- ESTADOS ---
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openModal, setOpenModal] = useState(false);
    const [openCompositeModal, setOpenCompositeModal] = useState(false);
    const [formData, setFormData] = useState({});
    const [editingProduct, setEditingProduct] = useState(null);
    const [error, setError] = useState('');
    const [categories, setCategories] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [brands, setBrands] = useState([]);
    const [loadingCatalogs, setLoadingCatalogs] = useState(true);
    const [gridKey, setGridKey] = useState(0);

    // --- CARGA DE DATOS ---
    const fetchProducts = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/products?timestamp=${new Date().getTime()}`);
            setProducts(response.data || []);
            setGridKey(prev => prev + 1);
        } catch (err) {
            setError("No se pudieron cargar los productos.");
            console.error("Error al obtener productos:", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCatalogs = async () => {
        setLoadingCatalogs(true);
        try {
            const [categoriesRes, suppliersRes, brandsRes] = await Promise.all([
                api.get('/categories'),
                api.get('/suppliers'),
                api.get('/brands')
            ]);
            setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
            setSuppliers(Array.isArray(suppliersRes.data) ? suppliersRes.data : []);
            setBrands(Array.isArray(brandsRes.data) ? brandsRes.data : []);
        } catch (error) {
            console.error("Error crítico al cargar catálogos:", error);
            setError("No se pudieron cargar las opciones del formulario.");
        } finally {
            setLoadingCatalogs(false);
        }
    };

    useEffect(() => {
        fetchProducts();
        fetchCatalogs();
    }, []);

    // --- MANEJADORES ---
    const handleOpenCreateModal = () => {
        setEditingProduct(null);
        setFormData({
            sku: '', name: '', description: '',
            category_id: '', supplier_id: '', brand_id: '',
            purchase_price: '', is_composite: false
        });
        setError('');
        setOpenModal(true);
    };

    const handleOpenEditModal = (product) => {
        setEditingProduct(product);
        setError('');
        if (product.is_composite) {
            setOpenCompositeModal(true);
        } else {
            setFormData({
                sku: product.sku || '',
                name: product.name || '',
                description: product.description || '',
                category_id: product.category_id || '',
                supplier_id: product.supplier_id || '',
                brand_id: product.brand_id || '',
                purchase_price: product.purchase_price || 0,
                is_composite: product.is_composite || false
            });
            setOpenModal(true);
        }
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setOpenCompositeModal(false);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        setFormData(prevState => ({ ...prevState, [name]: newValue }));
    };

    const handleSubmit = async () => {
        setError('');
        try {
            const payload = {
                ...formData,
                purchase_price: parseFloat(formData.purchase_price) || 0,
                category_id: formData.category_id ? parseInt(formData.category_id, 10) : null,
                supplier_id: formData.supplier_id ? parseInt(formData.supplier_id, 10) : null,
                brand_id: formData.brand_id ? parseInt(formData.brand_id, 10) : null,
                is_composite: !!formData.is_composite
            };
            if (editingProduct) {
                await api.put(`/products/${editingProduct.id}`, payload);
            } else {
                await api.post('/products', payload);
            }
            handleCloseModal();
            await fetchProducts();
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar el producto.');
        }
    };

    const handleSaveCompositeItems = async (items) => {
        try {
            await api.put(`/products/${editingProduct.id}/items`, { items });
            handleCloseModal();
            alert('Componentes guardados con éxito. El precio de compra ha sido recalculado.');
            await fetchProducts();
        } catch (err) {
            console.error("Error al guardar componentes:", err);
            alert('No se pudieron guardar los componentes.');
        }
    };

    const columns = [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'sku', headerName: 'SKU', width: 150 },
        { field: 'name', headerName: 'Nombre', width: 250 },
        { field: 'brand_name', headerName: 'Marca', width: 150 },
        { field: 'category_name', headerName: 'Categoría', width: 150 },
        { field: 'supplier_name', headerName: 'Proveedor', width: 150 },
        {
            field: 'purchase_price',
            headerName: 'Precio Compra',
            width: 150,
            type: 'number',
            renderCell: (params) => {
                const value = params.value;
                if (value == null || isNaN(Number(value))) return '';
                return `$${Number(value).toFixed(2)}`;
            }
        },
        { field: 'is_composite', headerName: 'Compuesto', type: 'boolean', width: 120 },
        {
            field: 'actions', headerName: 'Acciones', width: 150, sortable: false,
            renderCell: (params) => <Button variant="outlined" size="small" onClick={() => handleOpenEditModal(params.row)}>Editar</Button>
        },
    ];

    return (
        <Box sx={{ height: '85vh', width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Gestión de Productos</Typography>
                <Button variant="contained" onClick={handleOpenCreateModal}>Añadir Nuevo Producto</Button>
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <DataGrid
                key={gridKey}
                rows={products}
                columns={columns}
                loading={loading}
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
            />
            <Modal open={openModal} onClose={handleCloseModal}>
                <Box sx={style}>
                    <Typography variant="h6">{editingProduct ? 'Editar Producto' : 'Crear Nuevo Producto'}</Typography>
                    <TextField margin="normal" required fullWidth label="SKU" name="sku" value={formData.sku || ''} onChange={handleInputChange} />
                    <TextField margin="normal" required fullWidth label="Nombre" name="name" value={formData.name || ''} onChange={handleInputChange} />
                    <TextField margin="normal" fullWidth multiline rows={2} label="Descripción" name="description" value={formData.description || ''} onChange={handleInputChange} />
                    <TextField margin="normal" required fullWidth label="Precio de Compra" name="purchase_price" type="number" value={formData.purchase_price || ''} onChange={handleInputChange} />
                    {loadingCatalogs ? <CircularProgress sx={{ mt: 2 }} /> : (
                        <>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Categoría</InputLabel>
                                <Select name="category_id" value={formData.category_id || ''} label="Categoría" onChange={handleInputChange}>
                                    {categories.map(cat => <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Proveedor</InputLabel>
                                <Select name="supplier_id" value={formData.supplier_id || ''} label="Proveedor" onChange={handleInputChange}>
                                    {suppliers.map(sup => <MenuItem key={sup.id} value={sup.id}>{sup.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Marca</InputLabel>
                                <Select name="brand_id" value={formData.brand_id || ''} label="Marca" onChange={handleInputChange}>
                                    {brands.map(brand => <MenuItem key={brand.id} value={brand.id}>{brand.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </>
                    )}
                    <FormControlLabel
                        control={<Switch checked={!!formData.is_composite} onChange={handleInputChange} name="is_composite" />}
                        label="Es Producto Compuesto (Kit/Receta)"
                    />
                    {formData.is_composite && editingProduct && (
                        <Box mt={2}>
                            <Typography color="secondary">Guarda los cambios para poder gestionar los componentes de este producto.</Typography>
                        </Box>
                    )}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={handleCloseModal} sx={{ mr: 1 }}>Cancelar</Button>
                        <Button variant="contained" onClick={handleSubmit}>{editingProduct ? 'Guardar Cambios' : 'Crear'}</Button>
                    </Box>
                </Box>
            </Modal>
            {openCompositeModal && (
                <CompositeProductModal
                    open={openCompositeModal}
                    onClose={handleCloseModal}
                    product={editingProduct}
                    allProducts={products}
                    onSave={handleSaveCompositeItems}
                />
            )}
        </Box>
    );
};

export default ProductManagementPage;